

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getOnboardingState, markTourCompleted } from '@/services/onboardingService';
import {
  buildTourSteps,
  buildCatalogTourSteps,
  buildExploreTourSteps,
  buildSalesMappingTourSteps,
  buildSales2TourSteps,
  buildIndustryCategoryTourSteps,
  buildSku2TourSteps,
  buildDashboardBusinessTourSteps,
  buildMessagesTourSteps,
  useUserManagementTour,
  buildSkuSalesTourSteps,
  buildTeamManagementTourSteps,
} from '@/hooks/useDashboardTour';

const TourContext = createContext(null);

const sessionStarted = new Set();

const TOUR_MAP = {
  '/dashboard': {
    key: 'dashboard_business',
    buildSteps: buildDashboardBusinessTourSteps,
    delayMs: 800,
  },
  '/financial-dashboard': {
    key: 'dashboard',
    buildSteps: buildTourSteps,
    delayMs: 600,
  },
  '/explore': {
    key: 'explore',
    buildSteps: buildExploreTourSteps,
    delayMs: 600,
  },
  '/catalog': {
    key: 'catalog',
    buildSteps: buildCatalogTourSteps,
    delayMs: 600,
  },
  '/sales-mapping': {
    key: 'sales_mapping',
    buildSteps: buildSalesMappingTourSteps,
    delayMs: 700,
  },
  '/industry-categories': {
    key: 'industry_category',
    buildSteps: buildIndustryCategoryTourSteps,
    delayMs: 700,
  },
  '/transactions/sales-v2': {
    key: 'sales2',
    buildSteps: buildSales2TourSteps,
    delayMs: 800,
  },
  '/skus-v2': {
    key: 'sku2',
    buildSteps: buildSku2TourSteps,
    delayMs: 700,
  },
  '/messages': {
    key: 'messages',
    buildSteps: buildMessagesTourSteps,
    delayMs: 700,
  },
  '/secondary-sales/sku-sales-v2': {
    key: 'sku_sales_v2',
    buildSteps: buildSkuSalesTourSteps,
    delayMs: 800,
  },
  '/labels': {
    key: 'team_management',
    buildSteps: buildTeamManagementTourSteps,
    delayMs: 800,
  },
};


function makeOnPopoverRender(driverInstanceRef, totalSteps) {
  return (popover) => {
    const driverInstance = driverInstanceRef.current;
    if (!driverInstance) return;

    const titleEl = popover.title;
    const footerEl = popover.footer;

    if (titleEl) {
      const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
      titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach((n) => n.remove());

      const labelSpan = document.createElement('span');
      labelSpan.className = 'fd-title-text';
      labelSpan.textContent = 'Tutorials';
      titleEl.insertBefore(labelSpan, titleEl.firstChild);

      const counter = document.createElement('span');
      counter.className = 'fd-progress-counter';
      counter.textContent = `${currentIndex + 1} of ${totalSteps}`;
      titleEl.appendChild(counter);
    }

    if (footerEl) {
      const oldSkip = footerEl.querySelector('.fd-skip-btn');
      if (oldSkip) oldSkip.remove();

      const skipBtn = document.createElement('button');
      skipBtn.className = 'fd-skip-btn';
      skipBtn.textContent = 'Skip';
      skipBtn.type = 'button';
      skipBtn.addEventListener('click', () => driverInstance.destroy());
      footerEl.insertBefore(skipBtn, footerEl.firstChild);
    }
  };
}

export function TourProvider({ children, userId, companyId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeDriverRef = useRef(null);

  // Active tour details
  const [activeTourKey, setActiveTourKey] = useState(null);

  // Track checked pathnames to prevent infinite polling restarts on re-renders
  const checkedRoutesRef = useRef(new Set());

  // Instantiate the User Management multi-page hook (retains self-contained logic)
  const { startTour: startUmTour } = useUserManagementTour(userId);

  // Determine if a tour is configured for the current path
  const currentTourConfig = useMemo(() => {
    const path = Object.keys(TOUR_MAP).find((p) => location.pathname.startsWith(p));
    return path ? TOUR_MAP[path] : null;
  }, [location.pathname]);

  const destroyDriver = useCallback(() => {
    if (activeDriverRef.current) {
      try {
        activeDriverRef.current.destroy();
      } catch {
        // ignore
      }
      activeDriverRef.current = null;
    }
    setActiveTourKey(null);
  }, []);


  const launchSinglePageTour = useCallback((config, isManual = false) => {
    destroyDriver();

    const maxWait = 10000;
    const intervalMs = 150;
    let elapsed = 0;
    let cancelled = false;

    const pollInterval = setInterval(() => {
      if (cancelled) {
        clearInterval(pollInterval);
        return;
      }

      const steps = config.buildSteps();
      if (steps.length > 0) {
        clearInterval(pollInterval);

        setTimeout(() => {
          if (cancelled) return;

          const driverInstance = driver({
            animate: true,
            smoothScroll: true,
            overlayColor: 'rgba(0, 0, 0, 0.55)',
            overlayOpacity: 0.55,
            stagePadding: 8,
            stageRadius: 10,
            popoverClass: 'fd-tour-popover',
            showProgress: false,
            allowHTMLInPopover: true,
            nextBtnText: 'Next',
            doneBtnText: 'Done',
            closeBtnText: 'Skip',
            scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
            onPopoverRender: makeOnPopoverRender(activeDriverRef, steps.length),
            onNextClick: () => driverInstance.moveNext(),
            onDestroyStarted: () => driverInstance.destroy(),
            onDestroyed: () => {
              activeDriverRef.current = null;
              setActiveTourKey(null);
              // Save completion to backend
              if (companyId) {
                markTourCompleted(companyId, config.key).catch(() => { });
              }
            },
            steps,
          });

          activeDriverRef.current = driverInstance;
          setActiveTourKey(config.key);
          driverInstance.drive();
        }, config.delayMs ?? 600);
      } else {
        elapsed += intervalMs;
        if (elapsed >= maxWait) {
          clearInterval(pollInterval);
          if (companyId) {
            markTourCompleted(companyId, config.key).catch(() => { });
          }
        }
      }
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [companyId, destroyDriver]);

  useEffect(() => {
    if (!companyId || !currentTourConfig) return;

    const routeKey = `${location.pathname}:${companyId}`;
    if (checkedRoutesRef.current.has(routeKey)) return;
    checkedRoutesRef.current.add(routeKey);

    const sessionKey = `${companyId}:${currentTourConfig.key}`;
    if (sessionStarted.has(sessionKey)) return;

    let cleanupPoll = null;

    const autoStart = async () => {
      try {
        const state = await getOnboardingState(companyId);
        const isForceReset =
          window.location.search.includes('reset_tour=true') ||
          window.location.search.includes('reset_tours=true');

        if (state[currentTourConfig.key] === true && !isForceReset) return;

        sessionStarted.add(sessionKey);
        cleanupPoll = launchSinglePageTour(currentTourConfig);
      } catch (err) {
        console.warn('[tour] auto-start failed:', err);
      }
    };

    autoStart();

    return () => {
      if (cleanupPoll) cleanupPoll();
    };
  }, [location.pathname, companyId, currentTourConfig, launchSinglePageTour]);

  // Clean up driver on unmount
  useEffect(() => {
    return () => destroyDriver();
  }, [destroyDriver]);


  const startTour = useCallback(() => {
    const path = location.pathname;

    // Handle User Management multi-page routing
    if (path.startsWith('/user-role-management') || path.startsWith('/roles-and-permissions')) {
      startUmTour();
      return;
    }

    // Handle single-page tours
    if (currentTourConfig) {
      launchSinglePageTour(currentTourConfig, true);
    }
  }, [location.pathname, currentTourConfig, launchSinglePageTour, startUmTour]);

  const value = {
    startTour,
    isActive: !!activeTourKey,
    activeTourKey,
    stopTour: destroyDriver,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTourManager() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourManager must be used within a TourProvider');
  }
  return context;
}
